import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle, Home, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSLATIONS = {
  success: {
    en: "Booking Confirmed!", it: "Prenotazione Confermata!", es: "¡Reserva Confirmada!", fr: "Réservation Confirmée!",
    de: "Buchung Bestätigt!", zh: "预订已确认！", ru: "Бронирование подтверждено!", ar: "تم تأكيد الحجز!", ko: "예약 확정!"
  },
  thankYou: {
    en: "Thank you for your booking. A confirmation email has been sent to your address.",
    it: "Grazie per la tua prenotazione. Una email di conferma è stata inviata al tuo indirizzo.",
    es: "Gracias por tu reserva. Se ha enviado un correo de confirmación a tu dirección.",
    fr: "Merci pour votre réservation. Un email de confirmation a été envoyé à votre adresse.",
    de: "Vielen Dank für Ihre Buchung. Eine Bestätigungs-E-Mail wurde an Ihre Adresse gesendet.",
    zh: "感谢您的预订。确认邮件已发送至您的邮箱。",
    ru: "Спасибо за бронирование. Письмо с подтверждением отправлено на ваш адрес.",
    ar: "شكراً لحجزك. تم إرسال رسالة تأكيد إلى بريدك الإلكتروني.",
    ko: "예약해 주셔서 감사합니다. 확인 이메일이 발송되었습니다."
  },
  processing: {
    en: "Processing your payment...", it: "Elaborazione del pagamento...", es: "Procesando su pago...", fr: "Traitement de votre paiement...",
    de: "Zahlung wird verarbeitet...", zh: "正在处理您的付款...", ru: "Обработка платежа...", ar: "جاري معالجة الدفع...", ko: "결제 처리 중..."
  },
  failed: {
    en: "Payment Failed", it: "Pagamento Fallito", es: "Pago Fallido", fr: "Échec du Paiement",
    de: "Zahlung Fehlgeschlagen", zh: "支付失败", ru: "Ошибка оплаты", ar: "فشل الدفع", ko: "결제 실패"
  },
  failedMessage: {
    en: "Your payment could not be processed. Please try again or contact us for assistance.",
    it: "Il pagamento non è andato a buon fine. Riprova o contattaci per assistenza.",
    es: "No se pudo procesar su pago. Inténtelo de nuevo o contáctenos para obtener ayuda.",
    fr: "Votre paiement n'a pas pu être traité. Veuillez réessayer ou nous contacter pour obtenir de l'aide.",
    de: "Ihre Zahlung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.",
    zh: "无法处理您的付款。请重试或联系我们寻求帮助。",
    ru: "Ваш платеж не может быть обработан. Попробуйте еще раз или свяжитесь с нами.",
    ar: "لم يتم معالجة الدفع. يرجى المحاولة مرة أخرى أو الاتصال بنا للحصول على المساعدة.",
    ko: "결제를 처리할 수 없습니다. 다시 시도하거나 도움이 필요하시면 연락해 주세요."
  },
  pending: {
    en: "Payment Pending", it: "Pagamento in Attesa", es: "Pago Pendiente", fr: "Paiement en Attente",
    de: "Zahlung Ausstehend", zh: "付款待处理", ru: "Ожидание оплаты", ar: "الدفع معلق", ko: "결제 대기 중"
  },
  pendingMessage: {
    en: "Your payment is being processed. You will receive a confirmation email once completed.",
    it: "Il pagamento è in elaborazione. Riceverai una email di conferma al completamento.",
    es: "Su pago está siendo procesado. Recibirá un correo de confirmación una vez completado.",
    fr: "Votre paiement est en cours de traitement. Vous recevrez un email de confirmation une fois terminé.",
    de: "Ihre Zahlung wird verarbeitet. Sie erhalten eine Bestätigungs-E-Mail nach Abschluss.",
    zh: "您的付款正在处理中。完成后您将收到确认邮件。",
    ru: "Ваш платеж обрабатывается. Вы получите письмо с подтверждением после завершения.",
    ar: "يتم معالجة الدفع الخاص بك. ستتلقى رسالة تأكيد عند الانتهاء.",
    ko: "결제가 처리 중입니다. 완료되면 확인 이메일을 받으실 수 있습니다."
  },
  bookingDetails: {
    en: "Booking Details", it: "Dettagli Prenotazione", es: "Detalles de la Reserva", fr: "Détails de la Réservation",
    de: "Buchungsdetails", zh: "预订详情", ru: "Детали бронирования", ar: "تفاصيل الحجز", ko: "예약 상세"
  },
  confirmationNumber: {
    en: "Confirmation #", it: "N° Conferma", es: "N° Confirmación", fr: "N° Confirmation",
    de: "Bestätigungsnr.", zh: "确认号", ru: "№ подтверждения", ar: "رقم التأكيد", ko: "확인 번호"
  },
  checkIn: {
    en: "Check-in", it: "Check-in", es: "Entrada", fr: "Arrivée", de: "Anreise", zh: "入住", ru: "Заезд", ar: "الوصول", ko: "체크인"
  },
  checkOut: {
    en: "Check-out", it: "Check-out", es: "Salida", fr: "Départ", de: "Abreise", zh: "退房", ru: "Выезд", ar: "المغادرة", ko: "체크아웃"
  },
  totalPaid: {
    en: "Total Paid", it: "Totale Pagato", es: "Total Pagado", fr: "Total Payé",
    de: "Gesamtbetrag", zh: "已付总额", ru: "Оплачено", ar: "المبلغ المدفوع", ko: "결제 금액"
  },
  backToHome: {
    en: "Back to Home", it: "Torna alla Home", es: "Volver al Inicio", fr: "Retour à l'Accueil",
    de: "Zurück zur Startseite", zh: "返回首页", ru: "На главную", ar: "العودة للرئيسية", ko: "홈으로 돌아가기"
  },
  tryAgain: {
    en: "Try Again", it: "Riprova", es: "Intentar de Nuevo", fr: "Réessayer",
    de: "Erneut Versuchen", zh: "重试", ru: "Попробовать снова", ar: "حاول مرة أخرى", ko: "다시 시도"
  }
};

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading, paid, pending, failed
  const [bookingData, setBookingData] = useState(null);
  
  // Always use English for booking confirmation page
  const language = "en";

  const t = (key) => TRANSLATIONS[key]?.[language] || TRANSLATIONS[key]?.en || key;

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus("failed");
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/booking/checkout-status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === "paid") {
        setStatus("paid");
        setBookingData({
          confirmationNumber: data.metadata?.booking_number,
          guestName: data.metadata?.guest_name,
          checkIn: data.metadata?.check_in,
          checkOut: data.metadata?.check_out,
          amount: data.amount_total,
          currency: data.currency?.toUpperCase()
        });
      } else if (data.status === "open" || data.payment_status === "unpaid") {
        setStatus("pending");
      } else {
        setStatus("failed");
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-xl border border-[#E7E5E4] overflow-hidden">
          {/* Status Icon Header */}
          <div className={`p-8 text-center ${
            status === "paid" ? "bg-green-50" :
            status === "pending" ? "bg-yellow-50" :
            status === "failed" ? "bg-red-50" :
            "bg-[#F9F8F6]"
          }`}>
            {status === "loading" && (
              <Loader2 size={80} className="mx-auto text-[#D4AF37] animate-spin" />
            )}
            {status === "paid" && (
              <CheckCircle size={80} className="mx-auto text-green-500" />
            )}
            {status === "pending" && (
              <AlertTriangle size={80} className="mx-auto text-yellow-500" />
            )}
            {status === "failed" && (
              <XCircle size={80} className="mx-auto text-red-500" />
            )}
          </div>

          {/* Content */}
          <div className="p-8 text-center">
            {status === "loading" && (
              <>
                <h1 className="font-heading text-2xl text-[#1C1917] mb-2">{t("processing")}</h1>
              </>
            )}

            {status === "paid" && (
              <>
                <h1 className="font-heading text-3xl text-[#1C1917] mb-4">{t("success")}</h1>
                <p className="text-[#78716C] mb-6">{t("thankYou")}</p>

                {bookingData && (
                  <div className="bg-[#F9F8F6] rounded-lg p-6 text-left mb-6">
                    <h3 className="font-heading text-lg text-[#1C1917] mb-4 text-center">{t("bookingDetails")}</h3>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-[#78716C]">{t("confirmationNumber")}</span>
                        <span className="font-bold text-[#D4AF37]">#{bookingData.confirmationNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#78716C]">{t("checkIn")}</span>
                        <span className="font-medium">{bookingData.checkIn}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#78716C]">{t("checkOut")}</span>
                        <span className="font-medium">{bookingData.checkOut}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-[#E7E5E4] pt-4 mt-4">
                        <span className="font-heading text-xl font-bold">{t("totalPaid")}</span>
                        <span className="font-heading text-xl font-bold text-[#D4AF37]">
                          € {(bookingData.amount / 100).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <Link 
                  to="/" 
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8962E] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  <Home size={18} />
                  {t("backToHome")}
                </Link>
              </>
            )}

            {status === "pending" && (
              <>
                <h1 className="font-heading text-2xl text-[#1C1917] mb-4">{t("pending")}</h1>
                <p className="text-[#78716C] mb-6">{t("pendingMessage")}</p>
                <Link 
                  to="/" 
                  className="inline-flex items-center gap-2 bg-[#1C1917] hover:bg-[#292524] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  <Home size={18} />
                  {t("backToHome")}
                </Link>
              </>
            )}

            {status === "failed" && (
              <>
                <h1 className="font-heading text-2xl text-[#1C1917] mb-4">{t("failed")}</h1>
                <p className="text-[#78716C] mb-6">{t("failedMessage")}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link 
                    to="/#booking" 
                    className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#B8962E] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    {t("tryAgain")}
                  </Link>
                  <Link 
                    to="/" 
                    className="inline-flex items-center justify-center gap-2 bg-[#1C1917] hover:bg-[#292524] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    <Home size={18} />
                    {t("backToHome")}
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-[#F9F8F6] px-8 py-4 text-center">
            <p className="text-sm text-[#78716C]">Suite Lucina · Rome</p>
          </div>
        </div>
      </div>
    </div>
  );
}